// Ranking calculation utilities for pre-computed rankings
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  doc,
  setDoc,
  getDoc
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { shouldAutoExcludeResult } from './ageCategoryUtils.js';

// Cache collection names
const CACHE_COLLECTIONS = {
  CALCULATION_METADATA: 'cacheMetadata'
};

// Calculate comprehensive dancer rankings with all metrics
export async function calculateDancerRankings(dancerName) {
  console.log(`🎯 Calculating comprehensive rankings for: ${dancerName}`);
  
  try {
    // Get all dancer's competition results
    const dancerResults = await getDancerResults(dancerName);
    
    if (dancerResults.length === 0) {
      console.log(`⚠️ No results found for ${dancerName}`);
      return null;
    }

    // Group results by discipline/category/dance
    const groupedResults = groupResultsByCategory(dancerResults);
    
    // Calculate rankings for each group
    const disciplineOverview = [];
    
    for (const [key, results] of Object.entries(groupedResults)) {
      const [discipline, category, dance] = key.split('|');
      
      // Skip if any parameter is undefined (shouldn't happen after filtering, but safety check)
      if (!discipline || !category || !dance) {
        console.warn(`⚠️ Skipping calculation for invalid key: ${key}`);
        continue;
      }
      
      console.log(`📊 Calculating rankings for ${discipline} - ${category} - ${dance}`);
      
      // Get all dancers in this category for ranking comparison
      const allDancersData = await getAllDancersInCategory(discipline, category, dance);
      
      // Calculate current ranking and metrics
      const rankingMetrics = calculateRankingMetrics(dancerName, results, allDancersData);
      
      disciplineOverview.push({
        discipline,
        category,
        dance,
        ...rankingMetrics
      });
    }

    // Sort by discipline and category for consistent display
    disciplineOverview.sort((a, b) => {
      if (a.discipline !== b.discipline) return a.discipline.localeCompare(b.discipline);
      if (a.category !== b.category) return a.category.localeCompare(b.category);
      return a.dance.localeCompare(b.dance);
    });

    const precomputedData = {
      name: dancerName,
      results: dancerResults,
      disciplineOverview,
      lastCalculated: Timestamp.now(),
      calculationVersion: '1.0'
    };

    console.log(`✅ Calculated rankings for ${dancerName}:`, precomputedData);
    return precomputedData;

  } catch (error) {
    console.error(`❌ Error calculating rankings for ${dancerName}:`, error);
    throw error;
  }
}

// Get all competition results for a dancer
async function getDancerResults(dancerName) {
  const results = [];
  
  try {
    // Query old structure
    const oldQuery = query(collection(db, "competitions"));
    const oldSnapshot = await getDocs(oldQuery);

    // Process old structure results
    for (const doc of oldSnapshot.docs) {
      const comp = doc.data();
      if (comp.results && Array.isArray(comp.results)) {
        for (const result of comp.results) {
          // Check both possible field names for dancer name
          const resultDancerName = result.dancerName || result.name;
          if (resultDancerName === dancerName) {
            // For old structure, get discipline/category/dance from competition level OR result level
            const discipline = result.discipline || comp.discipline;
            const category = result.category || comp.category;
            const dance = result.dance || comp.dance;
            
            // Only include if we have all required fields
            // For non-Latin disciplines, handle missing or "null" dance values
            const isValidDance = dance && dance !== "null" && dance.trim() !== "";
            const isNonLatinDiscipline = discipline !== "Latin";
            
            if (discipline && category) {
              // For Latin, require a valid dance name
              if (discipline === "Latin" && !isValidDance) {
                continue; // Skip this result
              }
              
              // For non-Latin disciplines, use discipline name if dance is missing/null
              const finalDance = (isNonLatinDiscipline && (!isValidDance)) ? discipline : dance;
              
              // 🎯 NEW: Check if result is valid for ranking with age category validation
              const isValidForRanking = await isResultValidForRanking(result, comp, dancerName);
              
              results.push({
                ...result,
                competitionId: doc.id,
                timestamp: comp.timestamp || comp.date,
                competitionName: comp.name || doc.id,
                discipline: discipline,
                category: category,
                dance: finalDance,
                countsForRanking: isValidForRanking
              });
            }
          }
        }
      }
    }

    // Query new structure
    console.log('🔍 Querying new structure for dancer:', dancerName);
    const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
    console.log('📂 Found disciplines:', disciplinesSnapshot.size);
    
    for (const disciplineDoc of disciplinesSnapshot.docs) {
      const disciplineName = disciplineDoc.id;
      console.log('🎭 Processing discipline:', disciplineName);
      const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories"));
      console.log(`  📂 Found categories in ${disciplineName}:`, categoriesSnapshot.size);
      
      for (const categoryDoc of categoriesSnapshot.docs) {
        const categoryName = categoryDoc.id;
        console.log(`  📂 Processing category: ${disciplineName}/${categoryName}`);
        
        try {
          // The new structure is: disciplines/discipline/categories/category/competitions
          const competitionsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "competitions"));
          console.log(`    🏆 Found competitions in ${disciplineName}/${categoryName}:`, competitionsSnapshot.size);
          
          // Process new structure results
          for (const doc of competitionsSnapshot.docs) {
            const comp = doc.data();
            console.log(`      🏆 Processing competition: ${comp.name} with ${comp.results?.length || 0} results`);
            if (comp.results && Array.isArray(comp.results)) {
              for (const result of comp.results) {
                // Check both possible field names for dancer name
                const resultDancerName = result.dancerName || result.name;
                if (resultDancerName === dancerName) {
                  console.log(`        ✅ Found result for ${dancerName}: placement ${result.placement}, dance ${result.dance}`);
                  
                  // 🎯 NEW: Check if result is valid for ranking with age category validation
                  const isValidForRanking = await isResultValidForRanking(result, comp, dancerName);
                  
                  results.push({
                    ...result,
                    competitionId: doc.id,
                    timestamp: comp.timestamp || comp.date,
                    competitionName: comp.name || doc.id,
                    discipline: disciplineName,
                    category: categoryName,
                    dance: result.dance, // dance is stored in the result itself, not the path
                    countsForRanking: isValidForRanking
                  });
                }
              }
            }
          }
        } catch (error) {
          console.log(`⚠️ Error accessing competitions for ${disciplineName}/${categoryName}:`, error);
        }
      }
    }

    // Sort by timestamp (newest first)
    results.sort((a, b) => {
      const dateA = a.timestamp instanceof Timestamp ? a.timestamp.toDate() : new Date(a.timestamp);
      const dateB = b.timestamp instanceof Timestamp ? b.timestamp.toDate() : new Date(b.timestamp);
      return dateB - dateA;
    });

    console.log(`📊 Found ${results.length} results for ${dancerName}`);
    return results;

  } catch (error) {
    console.error(`❌ Error getting results for ${dancerName}:`, error);
    return [];
  }
}

// Check if result is valid for ranking (within last 365 days + age category exclusions)
async function isResultValidForRanking(result, competition, dancerName) {
  // First check basic time validity (within last 365 days)
  const competitionDate = competition.timestamp instanceof Timestamp 
    ? competition.timestamp.toDate() 
    : new Date(competition.timestamp || competition.date);
  
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  
  const isWithinTimeLimit = competitionDate >= oneYearAgo;
  
  // If not within time limit, immediately exclude
  if (!isWithinTimeLimit) {
    return false;
  }
  
  // If result is manually excluded or invalid, exclude
  if (result.excluded === true || result.invalid === true || result.isInvalid === true) {
    return false;
  }
  
  // 🎯 NEW: Check age category auto-exclusion
  if (dancerName && result.category) {
    try {
      const autoExclusionResult = await shouldAutoExcludeResult(
        dancerName,
        competitionDate,
        result.category
      );
      
      // If result should be auto-excluded, exclude from rankings
      if (autoExclusionResult.shouldExclude) {
        console.log(`🚫 Auto-excluding result for ${dancerName} in ${result.category} from ${competitionDate.toDateString()}`);
        console.log(`   Reason: ${autoExclusionResult.reason || 'Age category validation'}`);
        return false;
      }
    } catch (error) {
      console.warn(`⚠️ Error checking age category auto-exclusion for ${dancerName}:`, error);
      // In case of error, don't exclude (fail safe)
    }
  }
  
  return true;
}

// Simplified version for bulk operations where dancer name is not available
function isResultValidForRankingSimple(result, competition) {
  // Basic time validity (within last 365 days)
  const competitionDate = competition.timestamp instanceof Timestamp 
    ? competition.timestamp.toDate() 
    : new Date(competition.timestamp || competition.date);
  
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  
  const isWithinTimeLimit = competitionDate >= oneYearAgo;
  
  // If not within time limit, immediately exclude
  if (!isWithinTimeLimit) {
    return false;
  }
  
  // If result is manually excluded or invalid, exclude
  if (result.excluded === true || result.invalid === true || result.isInvalid === true) {
    return false;
  }
  
  // Note: Age category auto-exclusion is not checked in this simplified version
  // This will be applied in individual dancer calculations
  return true;
}

// Group results by discipline/category/dance
function groupResultsByCategory(results) {
  const grouped = {};
  
  results.forEach(result => {
    // Skip results with missing required fields
    if (!result.discipline || !result.category || !result.dance) {
      console.warn(`⚠️ Skipping result with missing fields:`, {
        discipline: result.discipline,
        category: result.category,
        dance: result.dance,
        competition: result.competitionName
      });
      return;
    }
    
    const key = `${result.discipline}|${result.category}|${result.dance}`;
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(result);
  });
  
  return grouped;
}

// Get all dancers in a specific category for ranking comparison
async function getAllDancersInCategory(discipline, category, dance) {
  const allDancers = new Map();
  
  try {
    // Query old structure
    const oldQuery = query(collection(db, "competitions"));
    const oldSnapshot = await getDocs(oldQuery);

    oldSnapshot.forEach((doc) => {
      const comp = doc.data();
      if (comp.results && Array.isArray(comp.results)) {
        comp.results.forEach((result) => {
          if (result.discipline === discipline && 
              result.category === category && 
              result.dance === dance &&
              isResultValidForRankingSimple(result, comp)) {
            
            const resultDancerName = result.dancerName || result.name;
            if (!allDancers.has(resultDancerName)) {
              allDancers.set(resultDancerName, []);
            }
            allDancers.get(resultDancerName).push({
              ...result,
              timestamp: comp.timestamp || comp.date,
              competitionName: comp.name || doc.id
            });
          }
        });
      }
    });

    // Query new structure
    const collectionRef = collection(db, "disciplines", discipline, "categories", category, "dances", dance, "competitions");
    const snapshot = await getDocs(collectionRef);
    
    snapshot.forEach((doc) => {
      const comp = doc.data();
      if (comp.results && Array.isArray(comp.results)) {
        comp.results.forEach((result) => {
          if (isResultValidForRankingSimple(result, comp)) {
            const resultDancerName = result.dancerName || result.name;
            if (!allDancers.has(resultDancerName)) {
              allDancers.set(resultDancerName, []);
            }
            allDancers.get(resultDancerName).push({
              ...result,
              timestamp: comp.timestamp || comp.date,
              competitionName: comp.name || doc.id
            });
          }
        });
      }
    });

    console.log(`📊 Found ${allDancers.size} dancers in ${discipline}/${category}/${dance}`);
    return allDancers;

  } catch (error) {
    console.error(`❌ Error getting all dancers in category:`, error);
    return new Map();
  }
}

// Calculate ranking metrics for a dancer
function calculateRankingMetrics(dancerName, results, allDancersData) {
  // Get top 6 results that count for ranking
  const validResults = results.filter(r => r.countsForRanking);
  const top6Results = validResults.slice(0, 6);
  
  if (top6Results.length === 0) {
    return {
      currentRank: null,
      currentDays: null,
      bestRank: null,
      bestDays: null,
      totalScore: 0,
      top6Results: []
    };
  }

  // Calculate total score from top 6 results
  const totalScore = top6Results.reduce((sum, result) => sum + (result.score || 0), 0);
  
  // Calculate all dancers' scores for ranking
  const dancerScores = [];
  
  for (const [name, dancerResults] of allDancersData.entries()) {
    const dancerValidResults = dancerResults.filter(r => isResultValidForRankingSimple(r, { timestamp: r.timestamp }));
    const dancerTop6 = dancerValidResults.slice(0, 6);
    const dancerScore = dancerTop6.reduce((sum, result) => sum + (result.score || 0), 0);
    
    if (dancerScore > 0) {
      dancerScores.push({ name, score: dancerScore });
    }
  }
  
  // Sort by score (highest first) to determine ranking
  dancerScores.sort((a, b) => b.score - a.score);
  
  // Find current rank
  const currentRank = dancerScores.findIndex(d => d.name === dancerName) + 1;
  
  // Calculate days in current rank
  const currentDays = calculateDaysInRank(dancerName, results, currentRank);
  
  // Calculate best rank and days (from historical data)
  const { bestRank, bestDays } = calculateBestRankMetrics(dancerName, results);
  
  return {
    currentRank: currentRank > 0 ? currentRank : null,
    daysHeld: currentDays,
    bestRank,
    bestDaysHeld: bestDays,
    totalPoints: totalScore,
    competitionCount: results.length,
    rankingCompetitionCount: top6Results.length,
    top6Results,
    totalDancers: dancerScores.length
  };
}

// Calculate days in current rank (simplified - would need historical ranking data for accuracy)
function calculateDaysInRank(dancerName, results, currentRank) {
  if (!results.length) return null;
  
  // For now, calculate days since last competition that affected ranking
  const latestValidResult = results.find(r => r.countsForRanking);
  if (!latestValidResult) return null;
  
  const latestDate = latestValidResult.timestamp instanceof Timestamp 
    ? latestValidResult.timestamp.toDate() 
    : new Date(latestValidResult.timestamp);
  
  const now = new Date();
  const diffTime = Math.abs(now - latestDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
}

// Calculate best rank metrics (simplified - would need historical ranking data for accuracy)
function calculateBestRankMetrics(dancerName, results) {
  // For now, assume best rank is current rank (would need historical data)
  // This is a placeholder - you'd need to track historical rankings over time
  
  if (!results.length) return { bestRank: null, bestDays: null };
  
  // Find the competition with the highest individual score as a proxy for best performance
  const bestResult = results.reduce((best, current) => {
    if (!best || (current.score || 0) > (best.score || 0)) {
      return current;
    }
    return best;
  }, null);
  
  if (!bestResult) return { bestRank: null, bestDays: null };
  
  // This is simplified - in reality you'd track when they achieved their best rank
  return {
    bestRank: 1, // Placeholder - would need historical ranking tracking
    bestDays: 30 // Placeholder - would need to track how long they held best rank
  };
}

// Calculate eternal cup winners for a specific year
export async function calculateEternalCupWinners(year) {
  console.log(`🏆 Calculating Eternal Cup Winners for ${year}`);
  
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year + 1, 0, 1);
  
  const categories = [
    // Latin categories
    "Solo Juvenile I", "Solo Juvenile II", "Solo Junior I",
    "Solo Junior II", "Solo Youth", "Solo Adult",
    // Contemporary categories
    "Solo Contemporary Juvenile I", "Solo Contemporary Juvenile II",
    "Solo Contemporary Junior I", "Solo Contemporary Junior II",
    "Solo Contemporary Youth", "Solo Contemporary Adult",
    // Hip Hop categories
    "Solo Hip Hop Juvenile I", "Solo Hip Hop Juvenile II",
    "Solo Hip Hop Junior I", "Solo Hip Hop Junior II",
    "Solo Hip Hop Youth", "Solo Hip Hop Adult"
  ];
  
  const winners = {};
  
  for (const category of categories) {
    let discipline;
    let actualCategory; // The category as stored in competitions
    
    if (category.includes("Contemporary")) {
      discipline = "Contemporary";
      actualCategory = category.replace("Solo Contemporary ", "Solo ");
    } else if (category.includes("Hip Hop")) {
      discipline = "Hip Hop";
      actualCategory = category.replace("Solo Hip Hop ", "Solo ");
    } else {
      discipline = "Latin";
      actualCategory = category;
    }
    
    console.log(`🎯 Calculating winners for ${discipline} - ${category} (searching for: ${actualCategory})`);
    
    const categoryWinners = await calculateCategoryWinners(discipline, actualCategory, year, yearStart, yearEnd);
    
    const key = `${discipline}-${category}`;
    winners[key] = {
      discipline,
      category,
      top3: categoryWinners,
      year
    };
  }
  
  const eternalCupData = {
    year,
    winners,
    lastCalculated: Timestamp.now(),
    calculationVersion: '1.0'
  };
  
  console.log(`✅ Calculated Eternal Cup Winners for ${year}`);
  return eternalCupData;
}

// Calculate winners for a specific category and year
async function calculateCategoryWinners(discipline, category, year, yearStart, yearEnd) {
  const dancerScores = new Map();
  
  try {
    // For Latin, combine all dances; for others, use the single dance
    const dances = discipline === "Latin" ? ["Samba", "ChaChaCha", "Rumba", "Pasodoble", "Jive"] : [discipline];
    
    for (const dance of dances) {
      // Query old structure
      const oldQuery = query(collection(db, "competitions"));
      const oldSnapshot = await getDocs(oldQuery);

      oldSnapshot.forEach((doc) => {
        const comp = doc.data();
        const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp || comp.date);
        
        if (compDate >= yearStart && compDate < yearEnd && comp.results && Array.isArray(comp.results)) {
          comp.results.forEach((result) => {
            // For Latin: check discipline, category, and specific dance
            // For Hip Hop/Contemporary/Commercial: check discipline and category only (ignore dance field)
            const matchesFilters = result.discipline === discipline && 
                                   result.category === category &&
                                   (discipline === "Latin" ? result.dance === dance : true);
            
            if (matchesFilters) {
              const resultDancerName = result.dancerName || result.name;
              if (!dancerScores.has(resultDancerName)) {
                dancerScores.set(resultDancerName, []);
              }
              dancerScores.get(resultDancerName).push({
                score: result.score || 0,
                competition: comp.name || doc.id,
                date: compDate,
                dance: result.dance || discipline
              });
            }
          });
        }
      });

      // Query new structure (only for Latin dances)
      if (discipline === "Latin") {
        try {
          const collectionRef = collection(db, "disciplines", discipline, "categories", category, "dances", dance, "competitions");
          const snapshot = await getDocs(collectionRef);
          
          snapshot.forEach((doc) => {
            const comp = doc.data();
            const compDate = comp.timestamp instanceof Timestamp ? comp.timestamp.toDate() : new Date(comp.timestamp || comp.date);
            
            if (compDate >= yearStart && compDate < yearEnd && comp.results && Array.isArray(comp.results)) {
              comp.results.forEach((result) => {
                const resultDancerName = result.dancerName || result.name;
                if (!dancerScores.has(resultDancerName)) {
                  dancerScores.set(resultDancerName, []);
                }
                dancerScores.get(resultDancerName).push({
                  score: result.score || 0,
                  competition: comp.name || doc.id,
                  date: compDate,
                  dance: dance
                });
              });
            }
          });
        } catch (error) {
          console.log(`⚠️ No hierarchical competitions found for ${discipline}/${category}/${dance}`);
        }
      }
    }

    // Calculate top scores for each dancer
    const finalScores = [];
    
    for (const [name, results] of dancerScores.entries()) {
      // Sort by score and take top 6
      const sortedResults = results.sort((a, b) => b.score - a.score);
      const top6 = sortedResults.slice(0, 6);
      const totalScore = top6.reduce((sum, result) => sum + result.score, 0);
      
      if (totalScore > 0) {
        finalScores.push({
          name,
          totalScore,
          top6Results: top6,
          totalCompetitions: results.length
        });
      }
    }

    // Sort by total score and return top 3
    finalScores.sort((a, b) => b.totalScore - a.totalScore);
    return finalScores.slice(0, 3);

  } catch (error) {
    console.error(`❌ Error calculating category winners:`, error);
    return [];
  }
}

// Save pre-computed rankings to Firestore
export async function savePrecomputedRankings(dancerData) {
  try {
    // Sanitize dancer name for use as document ID
    const sanitizedName = dancerData.name
      .replace(/\//g, '_') // Replace forward slashes with underscores
      .replace(/\\/g, '_') // Replace backslashes with underscores
      .replace(/[.#$[\]]/g, '_') // Replace other invalid Firestore characters
      .trim();
    
    const docRef = doc(db, 'precomputedRankings', sanitizedName);
    const dataWithNames = {
      ...dancerData,
      originalName: dancerData.name,
      sanitizedName: sanitizedName
    };
    await setDoc(docRef, dataWithNames);
    console.log(`💾 Saved precomputed rankings for ${dancerData.name}`);
  } catch (error) {
    console.error(`❌ Error saving precomputed rankings:`, error);
    throw error;
  }
}

// Save eternal cup winners to Firestore
export async function saveEternalCupWinners(year, winnersData) {
  try {
    const docRef = doc(db, 'eternalCupWinners', year.toString());
    await setDoc(docRef, winnersData);
    console.log(`💾 Saved eternal cup winners for ${year}`);
  } catch (error) {
    console.error(`❌ Error saving eternal cup winners:`, error);
    throw error;
  }
}

// Update metadata about last calculation
export async function updateCalculationMetadata() {
  try {
    const metadataRef = doc(db, CACHE_COLLECTIONS.CALCULATION_METADATA, 'lastCalculation');
    await setDoc(metadataRef, {
      lastCalculated: Timestamp.now(),
      calculationVersion: '1.0',
      status: 'completed'
    });
    console.log(`💾 Updated calculation metadata`);
  } catch (error) {
    console.error(`❌ Error updating metadata:`, error);
    throw error;
  }
}

// Check if rankings need updating (older than 24 hours)
export async function needsRankingUpdate() {
  try {
    const metadataRef = doc(db, CACHE_COLLECTIONS.CALCULATION_METADATA, 'lastCalculation');
    const metadataDoc = await getDoc(metadataRef);
    
    if (!metadataDoc.exists()) {
      console.log(`⚠️ No metadata found - rankings need initial calculation`);
      return true;
    }
    
    const metadata = metadataDoc.data();
    const lastCalculated = metadata.lastCalculated?.toDate();
    
    if (!lastCalculated) {
      console.log(`⚠️ No last calculated date - rankings need update`);
      return true;
    }
    
    const now = new Date();
    const hoursSinceUpdate = (now - lastCalculated) / (1000 * 60 * 60);
    
    const needsUpdate = hoursSinceUpdate > 24;
    console.log(`🕐 Hours since last update: ${hoursSinceUpdate.toFixed(1)} - Needs update: ${needsUpdate}`);
    
    return needsUpdate;
    
  } catch (error) {
    console.error(`❌ Error checking if rankings need update:`, error);
    return true; // Err on the side of updating
  }
}

// Check if cache exists and is recent enough
export const isCacheValid = async () => {
  try {
    const metadataDoc = doc(db, CACHE_COLLECTIONS.CALCULATION_METADATA, 'lastCalculation');
    const docSnap = await getDoc(metadataDoc);
    
    if (!docSnap.exists()) {
      console.log('🚨 No cache metadata found - cache needs initialization');
      return false;
    }
    
    const data = docSnap.data();
    const lastCalculation = data.lastCalculation?.toDate();
    
    if (!lastCalculation) {
      console.log('🚨 No lastCalculation timestamp - cache needs initialization');
      return false;
    }
    
    const hoursSinceUpdate = (new Date() - lastCalculation) / (1000 * 60 * 60);
    const isValid = hoursSinceUpdate < 24; // Cache valid for 24 hours
    
    console.log(`⏰ Cache age: ${hoursSinceUpdate.toFixed(1)} hours - Valid: ${isValid}`);
    
    return isValid;
    
  } catch (error) {
    console.error('❌ Error checking cache validity:', error);
    return false;
  }
};

// Automatic cache refresh system - runs in background
export const checkAndAutoRefreshCache = async () => {
  try {
    console.log('🔍 Checking if automatic cache refresh is needed...');
    
    // Check if cache is valid
    const isValid = await isCacheValid();
    
    if (!isValid) {
      console.log('🚀 Cache is stale, starting automatic refresh...');
      
      // Check if we're already in the middle of a recalculation
      const metadataRef = doc(db, CACHE_COLLECTIONS.CALCULATION_METADATA, 'lastCalculation');
      const metadataDoc = await getDoc(metadataRef);
      
      if (metadataDoc.exists()) {
        const metadata = metadataDoc.data();
        if (metadata.isRecalculating) {
          console.log('⏳ Cache recalculation already in progress, skipping...');
          return { started: false, reason: 'already_in_progress' };
        }
      }
      
      // Start automatic recalculation in background
      console.log('🎯 Starting automatic cache recalculation...');
      
      // Run recalculation without waiting for completion (background)
      forceRecalculateAll()
        .then((result) => {
          if (result.success) {
            console.log('✅ Automatic cache refresh completed successfully!');
          } else {
            console.log('❌ Automatic cache refresh failed');
          }
        })
        .catch((error) => {
          console.error('❌ Error in automatic cache refresh:', error);
        });
      
      return { started: true, reason: 'cache_stale' };
    } else {
      console.log('✅ Cache is fresh, no refresh needed');
      return { started: false, reason: 'cache_fresh' };
    }
    
  } catch (error) {
    console.error('❌ Error in automatic cache refresh:', error);
    return { started: false, reason: 'error', error: error.message };
  }
};

// Initialize cache if needed (call on app startup)
// 🔍 Check if cache needs update (lightweight check)
export const checkCacheStatus = async () => {
  try {
    const metadataRef = doc(db, 'cache', 'metadata');
    const metadataSnap = await getDoc(metadataRef);
    
    if (!metadataSnap.exists()) {
      console.log('⚠️ No cache found - needs initialization');
      return { 
        isStale: true, 
        hoursOld: null, 
        message: 'No cache found - needs initialization' 
      };
    }
    
    const metadata = metadataSnap.data();
    if (!metadata.lastCalculation) {
      console.log('⚠️ Cache metadata missing timestamp - needs initialization');
      return { 
        isStale: true, 
        hoursOld: null, 
        message: 'Cache metadata missing timestamp - needs initialization' 
      };
    }
    
    const lastCalc = metadata.lastCalculation.toDate();
    const hoursSinceUpdate = (new Date() - lastCalc) / (1000 * 60 * 60);
    
    if (hoursSinceUpdate > 24) {
      console.log(`⚠️ Cache is ${Math.round(hoursSinceUpdate)} hours old - needs update`);
      return { 
        isStale: true, 
        hoursOld: Math.round(hoursSinceUpdate), 
        message: `Cache is ${Math.round(hoursSinceUpdate)} hours old - needs update` 
      };
    }
    
    console.log(`✅ Cache is ${Math.round(hoursSinceUpdate)} hours old - still fresh`);
    return { 
      isStale: false, 
      hoursOld: Math.round(hoursSinceUpdate), 
      message: `Cache is ${Math.round(hoursSinceUpdate)} hours old - still fresh` 
    };
    
  } catch (error) {
    console.error('❌ Error checking cache status:', error);
    return { 
      isStale: true, 
      hoursOld: null, 
      message: `Error checking cache status: ${error.message}` 
    };
  }
};

export const initializeCacheIfNeeded = async () => {
  try {
    console.log('🔍 Checking if cache needs initialization...');
    
    const cacheValid = await isCacheValid();
    
    if (!cacheValid) {
      console.log('🚀 Initializing cache in background...');
      
      // Run cache initialization in background (don't await)
      forceRecalculateAll()
        .then(() => {
          console.log('✅ Background cache initialization completed');
        })
        .catch((error) => {
          console.error('❌ Background cache initialization failed:', error);
        });
        
      return { started: true, message: 'Cache initialization started in background' };
    } else {
      console.log('✅ Cache is valid, no initialization needed');
      return { started: false, message: 'Cache is already valid' };
    }
    
  } catch (error) {
    console.error('❌ Error in cache initialization check:', error);
    return { started: false, error: error.message };
  }
};

// Schedule daily cache updates (call this on app startup)
// ⚠️ Note: For production, cache updates should be handled by server-side processes
// This browser-based approach is a temporary solution
export const scheduleDailyCacheUpdates = () => {
  console.log('⚠️ Browser-based cache scheduling is not recommended for production');
  console.log('� Consider using Firebase Cloud Functions or manual admin triggers instead');
  
  // For now, just log the recommendation - don't actually schedule heavy operations
  // in users' browsers
};

// Force recalculation of all cached data (main function)
export const forceRecalculateAll = async () => {
  console.log('🔄 Force recalculating all cached data...');
  
  try {
    console.log('📊 Starting dancer rankings calculation...');
    
    // Get all dancers and calculate their rankings
    const competitionsRef = collection(db, 'competitions');
    const competitionsSnap = await getDocs(competitionsRef);
    
    const allDancers = new Set();
    
    // Collect all dancer names
    competitionsSnap.forEach(doc => {
      const competition = doc.data();
      if (competition.results && Array.isArray(competition.results)) {
        competition.results.forEach(result => {
          const dancerName = result.dancerName || result.name;
          if (dancerName) {
            allDancers.add(dancerName);
          }
        });
      }
    });
    
    console.log(`👥 Found ${allDancers.size} unique dancers`);
    
    // Calculate rankings for each dancer and save
    let processedCount = 0;
    for (const dancerName of allDancers) {
      const rankings = await calculateDancerRankings(dancerName);
      if (rankings) {
        await savePrecomputedRankings(rankings);
        processedCount++;
        
        if (processedCount % 10 === 0) {
          console.log(`✅ Processed ${processedCount}/${allDancers.size} dancers`);
        }
      }
    }
    
    console.log('🏆 Starting Eternal Cup calculations...');
    
    // Calculate Eternal Cup winners
    const winners = await calculateEternalCupWinners(new Date().getFullYear());
    if (winners) {
      await saveEternalCupWinners(new Date().getFullYear(), winners);
    }
    
    // Update metadata
    await updateCalculationMetadata();
    
    console.log(`🎉 Successfully recalculated cache for ${processedCount} dancers`);
    
    return { 
      success: true, 
      dancersProcessed: processedCount,
      message: `Recalculated cache for ${processedCount} dancers`
    };
    
  } catch (error) {
    console.error('Error in force recalculation:', error);
    throw error;
  }
};

// Force recalculate ONLY dancer rankings using Firebase Cloud Function
export const forceRecalculateDancerRankings = async () => {
  console.log('👥 Force recalculating dancer rankings via Cloud Function...');
  
  try {
    const functions = getFunctions();
    const recalculateFunction = httpsCallable(functions, 'recalculateCache', {
      timeout: 180000, // 3 minutes timeout (function completes in ~75 seconds)
    });
    
    const result = await recalculateFunction();
    console.log('✅ Dancer rankings recalculation completed:', result.data);
    
    return result.data;
  } catch (error) {
    console.error('❌ Error in dancer rankings recalculation:', error);
    throw error;
  }
};

// Force recalculate ONLY Eternal Cup rankings using Firebase Cloud Function
export const forceRecalculateEternalCupRankings = async () => {
  console.log('🏆 Force recalculating Eternal Cup rankings via Cloud Function...');
  
  try {
    const functions = getFunctions();
    const recalculateFunction = httpsCallable(functions, 'recalculateEternalCupRankings', {
      timeout: 180000, // 3 minutes timeout
    });
    
    const result = await recalculateFunction();
    console.log('✅ Eternal Cup rankings recalculation completed:', result.data);
    
    return result.data;
  } catch (error) {
    console.error('❌ Error in Eternal Cup rankings recalculation:', error);
    throw error;
  }
};

// Force recalculate ONLY Eternal Cup winners using Firebase Cloud Function
export const forceRecalculateEternalCupWinners = async () => {
  console.log('🥇 Force recalculating Eternal Cup winners via Cloud Function...');
  
  try {
    const functions = getFunctions();
    const calculateFunction = httpsCallable(functions, 'calculateEternalCupWinners');
    
    const result = await calculateFunction();
    console.log('✅ Eternal Cup winners calculation completed:', result.data);
    
    return result.data;
  } catch (error) {
    console.error('❌ Error in Eternal Cup winners calculation:', error);
    throw error;
  }
};

// Get cached dancer rankings (fast)
export const getCachedDancerRankings = async (dancerName) => {
  try {
    // Sanitize dancer name for document ID lookup
    const sanitizedName = dancerName
      .replace(/\//g, '_')
      .replace(/\\/g, '_')
      .replace(/[.#$[\]]/g, '_')
      .trim();
    
    // Try to get from cache first using document ID
    const docRef = doc(db, 'precomputedRankings', sanitizedName);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      console.log(`🚀 Found cached rankings for ${dancerName}`);
      
      // Check if cached data has both rankings AND results
      if (data.disciplineOverview && data.disciplineOverview.length > 0) {
        console.log(`✅ Using cached ranking calculations for ${dancerName}`);
        return {
          disciplineOverview: data.disciplineOverview,
          lastCalculated: data.lastCalculated,
          calculationVersion: data.calculationVersion
        };
      } else {
        console.log(`⚠️ Cached data incomplete for ${dancerName}, will calculate live...`);
        return null;
      }
    }
    
    // No cache found
    console.log(`⚠️ No cached data for ${dancerName}, will calculate live...`);
    return null;
    
  } catch (error) {
    console.error('Error getting cached dancer rankings:', error);
    // Return null to trigger live calculation
    return null;
  }
};

// Save cached dancer rankings (24-hour cache)
export const saveCachedDancerRankings = async (dancerName, rankingData) => {
  try {
    const sanitizedName = dancerName.replace(/[\/\\]/g, '_');
    const docRef = doc(db, 'precomputedRankings', sanitizedName);
    
    await setDoc(docRef, {
      ...rankingData,
      lastCalculated: Timestamp.fromDate(rankingData.lastCalculated || new Date()),
      calculationVersion: rankingData.calculationVersion || '1.0'
    });
    
    console.log(`💾 Successfully saved cache for ${dancerName}`);
    return true;
  } catch (error) {
    console.error(`❌ Error saving cache for ${dancerName}:`, error);
    return false;
  }
};

// Get cached Eternal Cup Winners (fast)
export const getCachedEternalCupWinners = async () => {
  try {
    console.log('🔍 Loading cached Eternal Cup Winners...');
    
    const years = [2025, 2024, 2023];
    const allWinnersData = {};
    
    // Load data for all years from the eternalCupWinners collection
    for (const year of years) {
      try {
        const docRef = doc(db, 'eternalCupWinners', year.toString());
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log(`✅ Found cached data for ${year}:`, Object.keys(data.winners || {}).length, 'categories');
          allWinnersData[year] = data;
        } else {
          console.log(`⚠️ No cached data found for ${year}`);
          allWinnersData[year] = 'PENDING';
        }
      } catch (error) {
        console.error(`❌ Error loading data for ${year}:`, error);
        allWinnersData[year] = 'PENDING';
      }
    }
    
    console.log('🏆 Loaded Eternal Cup Winners for years:', Object.keys(allWinnersData));
    return allWinnersData;
    
  } catch (error) {
    console.error('Error getting cached Eternal Cup winners:', error);
    
    // Fallback: try to calculate for current year only
    try {
      const currentYear = new Date().getFullYear();
      console.log(`🔄 Fallback: calculating live data for ${currentYear}...`);
      const winners = await calculateEternalCupWinners(currentYear);
      return { [currentYear]: { year: currentYear, winners } };
    } catch (fallbackError) {
      console.error('Fallback calculation also failed:', fallbackError);
      return {};
    }
  }
};
